import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    Link,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { fetchFeatureFlags } from '../../helpers/featureFlags';
import { applyEmail, applyName } from '../../helpers/captureMode';
import { normalizeAdminPlanNames } from '../../helpers/subscription';

const PLAN_OPTIONS = [
    { value: 'Free', label: 'Free / Começando' },
    { value: 'Growing', label: 'Growing / Crescendo' },
    { value: 'Pro', label: 'Pro / Profissional' },
    { value: 'Unlimited', label: 'Farm / Unlimited' },
];

function FeatureFlagsAdminPage() {
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [selectedKey, setSelectedKey] = useState('');
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [addUserUuid, setAddUserUuid] = useState('');

    const loadFlags = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/feature-flags');
            const list = response?.data?.data || [];
            setFlags(list);
            if (!selectedKey && list.length > 0) {
                setSelectedKey(list[0].key);
            }
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Não foi possível carregar as flags.', 'error');
        } finally {
            setLoading(false);
        }
    }, [selectedKey]);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (cancelled || !user || typeof user.is_master !== 'boolean') {
                    return;
                }
                patchStoredUser({ is_master: user.is_master });
                setIsMaster(user.is_master);
            })
            .catch(() => setIsMaster(false))
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isMaster || checkingAccess) {
            return undefined;
        }
        loadFlags();
        api.get('/users', { params: { page: 1, limit: 200 } })
            .then((response) => {
                const list = response?.data?.data || response?.data?.items || [];
                setUsers(Array.isArray(list) ? list : []);
            })
            .catch(() => setUsers([]));
        return undefined;
    }, [isMaster, checkingAccess, loadFlags]);

    useEffect(() => {
        const current = flags.find((item) => item.key === selectedKey);
        if (!current) {
            setForm(null);
            return;
        }
        setForm({
            name: current.name || '',
            description: current.description || '',
            enabled: Boolean(current.enabled),
            is_beta: Boolean(current.is_beta),
            show_in_menu: Boolean(current.show_in_menu),
            plan_names: normalizeAdminPlanNames(current.plan_names),
            allowlist: Array.isArray(current.allowlist) ? current.allowlist : [],
        });
        setAddUserUuid('');
    }, [flags, selectedKey]);

    if (checkingAccess) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    const handleSave = async () => {
        if (!form || !selectedKey) {
            return;
        }
        setSaving(true);
        try {
            const response = await api.put(`/feature-flags/${selectedKey}`, {
                name: form.name,
                description: form.description,
                enabled: form.enabled,
                is_beta: form.is_beta,
                show_in_menu: form.show_in_menu,
                plan_names: form.plan_names,
            });
            const updated = response?.data?.data;
            if (updated?.key) {
                setFlags((prev) => prev.map((flag) => (flag.key === updated.key ? updated : flag)));
            } else {
                await loadFlags();
            }
            await fetchFeatureFlags();
            Swal.fire('Salvo', 'Feature flag atualizada.', 'success');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao salvar.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleAddUser = async () => {
        if (!addUserUuid || !selectedKey) {
            return;
        }
        try {
            await api.post(`/feature-flags/${selectedKey}/users`, { user_uuid: addUserUuid });
            await loadFlags();
            await fetchFeatureFlags();
            setAddUserUuid('');
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.errors?.user_uuid || error?.response?.data?.error || 'Falha ao adicionar.', 'error');
        }
    };

    const handleRemoveUser = async (userUuid) => {
        try {
            await api.delete(`/feature-flags/${selectedKey}/users/${userUuid}`);
            await loadFlags();
            await fetchFeatureFlags();
        } catch (error) {
            Swal.fire('Erro', error?.response?.data?.error || 'Falha ao remover.', 'error');
        }
    };

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin" underline="hover" color="inherit">
                    Admin
                </Link>
                <Typography color="text.primary">Feature flags</Typography>
            </Breadcrumbs>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Feature flags
            </Typography>

            {loading ? (
                <CircularProgress />
            ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Paper sx={{ p: 2, minWidth: 260 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Funcionalidades
                        </Typography>
                        <Stack spacing={1}>
                            {flags.map((flag) => (
                                <Button
                                    key={flag.key}
                                    variant={selectedKey === flag.key ? 'contained' : 'outlined'}
                                    onClick={() => setSelectedKey(flag.key)}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                >
                                    {flag.name}
                                    {flag.is_beta ? (
                                        <Chip size="small" label="beta" sx={{ ml: 1 }} />
                                    ) : null}
                                </Button>
                            ))}
                        </Stack>
                    </Paper>

                    {form ? (
                        <Paper sx={{ p: 2, flex: 1 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {selectedKey}
                            </Typography>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Nome"
                                value={form.name}
                                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Descrição"
                                multiline
                                minRows={2}
                                value={form.description}
                                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.enabled}
                                        onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                                    />
                                }
                                label="Habilitada"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.is_beta}
                                        onChange={(e) => setForm((prev) => ({ ...prev, is_beta: e.target.checked }))}
                                    />
                                }
                                label="Beta feature"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.show_in_menu}
                                        onChange={(e) => setForm((prev) => ({ ...prev, show_in_menu: e.target.checked }))}
                                    />
                                }
                                label="Exibir no menu"
                            />

                            <FormControl fullWidth margin="normal">
                                <InputLabel id="plans-label">Planos liberados</InputLabel>
                                <Select
                                    labelId="plans-label"
                                    multiple
                                    value={form.plan_names}
                                    onChange={(e) => setForm((prev) => ({ ...prev, plan_names: e.target.value }))}
                                    input={<OutlinedInput label="Planos liberados" />}
                                    renderValue={(selected) => selected.join(', ')}
                                >
                                    {PLAN_OPTIONS.map((plan) => (
                                        <MenuItem key={plan.value} value={plan.value}>
                                            {plan.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {!form.is_beta ? (
                                <Alert severity="info" sx={{ my: 2 }}>
                                    Fora do beta, apenas os planos selecionados têm acesso. A allowlist de usuários não conta.
                                </Alert>
                            ) : (
                                <Alert severity="warning" sx={{ my: 2 }}>
                                    Em beta: acesso por plano liberado ou usuário na lista de testadores.
                                </Alert>
                            )}

                            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mb: 3 }}>
                                {saving ? 'Salvando…' : 'Salvar flag'}
                            </Button>

                            {form.is_beta ? (
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                        Testadores (allowlist)
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel id="user-add-label">Usuário</InputLabel>
                                            <Select
                                                labelId="user-add-label"
                                                value={addUserUuid}
                                                label="Usuário"
                                                onChange={(e) => setAddUserUuid(e.target.value)}
                                            >
                                                {users.map((user) => (
                                                    <MenuItem key={user.uuid} value={user.uuid}>
                                                        {applyName(user.name)} — {applyEmail(user.email)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button variant="outlined" onClick={handleAddUser} disabled={!addUserUuid}>
                                            Adicionar
                                        </Button>
                                    </Stack>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Nome</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell align="right">Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(form.allowlist || []).map((row) => (
                                                    <TableRow key={row.user_uuid}>
                                                        <TableCell>{row.user_name}</TableCell>
                                                        <TableCell>{row.user_email}</TableCell>
                                                        <TableCell align="right">
                                                            <Button size="small" color="error" onClick={() => handleRemoveUser(row.user_uuid)}>
                                                                Remover
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {(form.allowlist || []).length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={3}>Nenhum testador ainda.</TableCell>
                                                    </TableRow>
                                                ) : null}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ) : null}
                        </Paper>
                    ) : null}
                </Stack>
            )}
        </Box>
    );
}

export default FeatureFlagsAdminPage;
